import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [referralCode, setReferralCode] = useState("");
  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const code = query.get("referralCode");
    if (code) {
      setReferralCode(code);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (
      !formData.userName ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error("All fields are required");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        "https://bioopay-full-backend.onrender.com/api/v1/users/register/send-code",
        {
          userName: formData.userName,
          email: formData.email,
          password: formData.password,
          referralCode: referralCode || formData.referralCode,
        }
      );

      if (response.data.success) {
        toast.success("Verification code sent to your email");
        setShowVerificationModal(true);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Verification failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "https://bioopay-full-backend.onrender.com/api/v1/users/verify-email",
        {
          email: formData.email,
          code: verificationCode,
        }
      );

      if (response.data.success) {
        toast.success("Email verified successfully!");
        setIsVerified(true);
        setShowVerificationModal(false);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Verification failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        "https://bioopay-full-backend.onrender.com/api/v1/users/register",
        {
          email: formData.email,
        }
      );

      if (response.data.success) {
        toast.success("Registration successful!");
        navigate("/login");
      }
    } catch (error) {
      const message = error.response?.data?.message || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.mainContainer}>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastStyle={{
          backgroundColor: "#1E293B",
          color: "#F8FAFC",
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
        }}
      />

      {/* Verification Modal */}
      {showVerificationModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Verify Your Email</h3>
            <p style={styles.modalText}>
              We've sent a 6-digit verification code to {formData.email}
            </p>

            <div style={styles.codeInputContainer}>
              {Array.from({ length: 6 }).map((_, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  required
                  value={verificationCode[index] || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    const newCode = verificationCode.split("");
                    newCode[index] = value;
                    setVerificationCode(newCode.join("").slice(0, 6));

                    if (value && index < 5) {
                      const nextInput = e.target.nextElementSibling;
                      if (nextInput) nextInput.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !e.target.value && index > 0) {
                      const prevInput = e.target.previousElementSibling;
                      if (prevInput) prevInput.focus();
                    } else if (e.key === "ArrowLeft" && index > 0) {
                      e.preventDefault();
                      const prevInput = e.target.previousElementSibling;
                      if (prevInput) prevInput.focus();
                    } else if (e.key === "ArrowRight" && index < 5) {
                      e.preventDefault();
                      const nextInput = e.target.nextElementSibling;
                      if (nextInput) nextInput.focus();
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  style={styles.codeInput}
                  autoComplete="off"
                />
              ))}
            </div>

            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowVerificationModal(false)}
                style={styles.modalSecondaryButton}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                style={{
                  ...styles.modalPrimaryButton,
                  ...((loading || verificationCode.length !== 6) &&
                    styles.submitButtonDisabled),
                }}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.registerContainer}>
        <div style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoAccent}>Bloo</span>pay
          </div>
          <h2 style={styles.title}>
            {isVerified ? "Complete Registration" : "Create Your Account"}
          </h2>
          <p style={styles.subtitle}>
            {isVerified
              ? "Click register to complete your account"
              : "Fill all fields to get started"}
          </p>
        </div>

        <form
          onSubmit={isVerified ? handleRegister : handleSendVerification}
          style={styles.form}
        >
          <div style={styles.inputGroup}>
            <label style={styles.label}>Username</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </span>
              <input
                name="userName"
                type="text"
                required
                value={formData.userName}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter your username"
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email Address</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <path d="M22 6l-10 7L2 6"></path>
                </svg>
              </span>
              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </span>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
              >
                {showPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"></path>
                    <path d="M1 1l22 22"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm Password</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </span>
              <input
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                style={styles.input}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Referral Code (optional)</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </span>
              <input
                name="referralCode"
                type="text"
                value={referralCode || formData.referralCode}
                onChange={(e) => {
                  if (referralCode) {
                    setReferralCode(e.target.value);
                  } else {
                    handleChange(e);
                  }
                }}
                style={styles.input}
                placeholder="Enter referral code (if any)"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading && styles.submitButtonDisabled),
            }}
          >
            {loading ? (
              <>
                <svg
                  style={styles.spinner}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
                {isVerified ? "Registering..." : "Sending Verification..."}
              </>
            ) : isVerified ? (
              "Complete Registration"
            ) : (
              "Send Verification Code"
            )}
          </button>
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <div style={styles.dividerText}>Already have an account?</div>
          <div style={styles.dividerLine}></div>
        </div>

        <button onClick={() => navigate("/login")} style={styles.loginButton}>
          Sign In
        </button>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

const styles = {
  mainContainer: {
    fontFamily: "'Inter', sans-serif",
    backgroundColor: "#0a0a0a",
    color: "#e0e0e0",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    lineHeight: 1.6,
  },
  registerContainer: {
    width: "100%",
    maxWidth: "480px",
    padding: "30px",
    backgroundColor: "#1a1a1a",
    borderRadius: "15px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    position: "relative",
    overflow: "hidden",
    margin: "20px 0",
    "@media (max-width: 480px)": {
      padding: "25px 20px",
      borderRadius: "12px",
    },
    "::before": {
      content: '""',
      position: "absolute",
      top: "-100px",
      right: "-100px",
      width: "300px",
      height: "300px",
      background:
        "radial-gradient(circle, rgba(138,43,226,0.15) 0%, rgba(0,0,0,0) 70%)",
      zIndex: 0,
    },
  },
  header: {
    textAlign: "center",
    marginBottom: "2rem",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      marginBottom: "1.5rem",
    },
  },
  logo: {
    fontSize: "2.2rem",
    fontWeight: 700,
    marginBottom: "1.5rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "@media (max-width: 480px)": {
      fontSize: "1.8rem",
      marginBottom: "1rem",
    },
  },
  logoAccent: {
    fontWeight: 800,
    marginRight: "0.3rem",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "0.5rem",
    "@media (max-width: 480px)": {
      fontSize: "1.5rem",
    },
  },
  subtitle: {
    fontSize: "0.95rem",
    color: "#b0b0b0",
    fontWeight: 400,
    "@media (max-width: 480px)": {
      fontSize: "0.85rem",
    },
  },
  form: {
    position: "relative",
    zIndex: 1,
  },
  inputGroup: {
    marginBottom: "1.2rem",
    "@media (max-width: 480px)": {
      marginBottom: "1rem",
    },
  },
  label: {
    display: "block",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#b0b0b0",
    marginBottom: "0.6rem",
    "@media (max-width: 480px)": {
      fontSize: "0.9rem",
      marginBottom: "0.5rem",
    },
  },
  inputWrapper: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "14px 14px 14px 44px",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "0.95rem",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    "::placeholder": {
      color: "#555555",
      fontSize: "0.9rem",
    },
    ":focus": {
      borderColor: "#8a2be2",
      boxShadow: "0 0 0 3px rgba(138, 43, 226, 0.2)",
    },
    "@media (max-width: 480px)": {
      padding: "12px 12px 12px 40px",
      fontSize: "0.9rem",
    },
  },
  inputIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#b0b0b0",
    fontSize: "1.2rem",
    "@media (max-width: 480px)": {
      left: "12px",
    },
  },
  passwordToggle: {
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#b0b0b0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "none",
    border: "none",
    padding: "0",
    ":hover": {
      color: "#ffffff",
    },
    "@media (max-width: 480px)": {
      right: "12px",
    },
  },
  submitButton: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    color: "white",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    boxShadow: "0 5px 20px rgba(138, 43, 226, 0.3)",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "0.5rem",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(138, 43, 226, 0.5)",
    },
    "@media (max-width: 480px)": {
      padding: "12px",
      fontSize: "0.95rem",
    },
  },
  submitButtonDisabled: {
    opacity: "0.7",
    cursor: "not-allowed",
    transform: "none !important",
  },
  spinner: {
    animation: "spin 1s linear infinite",
    marginRight: "0.5rem",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "1.5rem 0",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      margin: "1.2rem 0",
    },
  },
  dividerLine: {
    flex: "1",
    height: "1px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  dividerText: {
    padding: "0 1rem",
    color: "#b0b0b0",
    fontSize: "0.85rem",
    fontWeight: 500,
    "@media (max-width: 480px)": {
      fontSize: "0.8rem",
    },
  },
  loginButton: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    color: "white",
    fontWeight: 500,
    fontSize: "0.95rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "@media (max-width: 480px)": {
      padding: "10px",
      fontSize: "0.9rem",
    },
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#1a1a1a",
    padding: "25px",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "400px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
    "@media (max-width: 480px)": {
      padding: "20px",
      width: "95%",
    },
  },
  modalTitle: {
    color: "#ffffff",
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1rem",
    textAlign: "center",
    "@media (max-width: 480px)": {
      fontSize: "1.3rem",
    },
  },
  modalText: {
    color: "#b0b0b0",
    fontSize: "0.95rem",
    marginBottom: "1.5rem",
    textAlign: "center",
    "@media (max-width: 480px)": {
      fontSize: "0.85rem",
      marginBottom: "1.2rem",
    },
  },
  modalButtons: {
    display: "flex",
    gap: "1rem",
    marginTop: "1.5rem",
    "@media (max-width: 480px)": {
      marginTop: "1.2rem",
    },
  },
  modalPrimaryButton: {
    flex: 1,
    padding: "12px",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    color: "white",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(138, 43, 226, 0.5)",
    },
    "@media (max-width: 480px)": {
      padding: "10px",
    },
  },
  modalSecondaryButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "transparent",
    color: "#b0b0b0",
    fontWeight: 600,
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
    },
    "@media (max-width: 480px)": {
      padding: "10px",
    },
  },
  codeInputContainer: {
    display: "flex",
    gap: "0.5rem",
    justifyContent: "center",
    "@media (max-width: 480px)": {
      gap: "0.3rem",
    },
  },
  codeInput: {
    width: "40px",
    height: "50px",
    textAlign: "center",
    fontSize: "1.2rem",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    color: "white",
    outline: "none",
    transition: "all 0.3s ease",
    ":focus": {
      borderColor: "#8a2be2",
      boxShadow: "0 0 0 3px rgba(138, 43, 226, 0.2)",
    },
    "@media (max-width: 480px)": {
      width: "35px",
      height: "45px",
      fontSize: "1.1rem",
    },
  },
};

export default Register;
